import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useFirestore } from '../contexts/FirestoreContext';
import { doc, getDoc } from 'firebase/firestore';
import { Card, Tabs, Button, Space, Typography, Badge, Tag, Alert, Switch, Tooltip } from 'antd';
import { 
  RobotOutlined,
  AlertOutlined, 
  NodeIndexOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  SettingOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import AutomatedTaskService from '../services/automatedTaskService';
import SmartEscalationService from '../services/smartEscalationService';
import TaskDependencyMapper from './TaskDependencyMapper';

const { Title, Text } = Typography;

/**
 * Phase 2 Automation Panel - Complete Implementation
 * 
 * Features:
 * 1. Automated Task Completion
 * 2. Smart Escalation Engine  
 * 3. Task Dependency Mapping
 */
const Phase2AutomationPanel = ({ incidentId, incidentData }) => {
  const { currentUser } = useAuth();
  const { db } = useFirestore();
  const [userOrgId, setUserOrgId] = useState(null);
  const [automationEnabled, setAutomationEnabled] = useState(true);
  const [escalationEnabled, setEscalationEnabled] = useState(true);
  const [automationStatus, setAutomationStatus] = useState(null);
  const [escalationStatus, setEscalationStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('automation');

  // Service instances
  const [automationService, setAutomationService] = useState(null);
  const [escalationService, setEscalationService] = useState(null);

  // Get user's orgId
  useEffect(() => {
    const fetchUserOrgId = async () => {
      if (!currentUser?.uid || !db) return;
      
      try {
        const userDocRef = doc(db, 'c3_users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          setUserOrgId(userDoc.data().orgId);
        }
      } catch (error) {
        console.error('Error fetching user orgId:', error);
      }
    };
    
    fetchUserOrgId();
  }, [currentUser?.uid, db]);

  // Initialize services
  useEffect(() => {
    if (db && incidentId && userOrgId) {
      initializeServices();
    }
  }, [db, incidentId, userOrgId]);

  const initializeServices = async () => {
    try {
      setLoading(true);

      // Initialize Automated Task Service
      const autoService = new AutomatedTaskService(db);
      setAutomationService(autoService);

      // Initialize Smart Escalation Service
      const escalService = new SmartEscalationService(db);
      setEscalationService(escalService);

      // Start automation if enabled (with error handling to prevent loops)
      if (automationEnabled) {
        try {
          await autoService.initializeAutomation(incidentId, userOrgId);
        } catch (automationError) {
          console.warn('⚠️ Could not initialize task automation:', automationError.message);
          // Continue without throwing to prevent infinite loops
        }
      }

      // Start escalation monitoring if enabled (with error handling to prevent loops)
      if (escalationEnabled) {
        try {
          await escalService.initializeEscalationMonitoring(incidentId, userOrgId);
        } catch (escalationError) {
          console.warn('⚠️ Could not initialize escalation monitoring:', escalationError.message);
          // Continue without throwing to prevent infinite loops
        }
      }

      // Load initial status
      await refreshStatus();

      console.log('🚀 Phase 2 automation panel initialized successfully');

    } catch (error) {
      console.error('Error initializing Phase 2 services:', error);
      // Don't throw to prevent infinite loops
    } finally {
      setLoading(false);
    }
  };

  const refreshStatus = async () => {
    if (!automationService || !escalationService) return;

    try {
      // Get automation status
      const autoStatus = await automationService.getAutomationStatus(incidentId, userOrgId);
      setAutomationStatus(autoStatus);

      // Get escalation status
      const escalStatus = await escalationService.getEscalationStatus(incidentId, userOrgId);
      setEscalationStatus(escalStatus);

    } catch (error) {
      console.error('Error refreshing status:', error);
    }
  };

  const toggleAutomation = async (enabled) => {
    try {
      setAutomationEnabled(enabled);

      if (enabled && automationService) {
        await automationService.initializeAutomation(incidentId, userOrgId);
        console.log('✅ Automation enabled');
      } else if (automationService) {
        automationService.cleanup(incidentId);
        console.log('⏸️ Automation disabled');
      }

      await refreshStatus();

    } catch (error) {
      console.error('Error toggling automation:', error);
    }
  };

  const toggleEscalation = async (enabled) => {
    try {
      setEscalationEnabled(enabled);

      if (enabled && escalationService) {
        await escalationService.initializeEscalationMonitoring(incidentId, userOrgId);
        console.log('🚨 Escalation monitoring enabled');
      } else if (escalationService) {
        escalationService.cleanup(incidentId);
        console.log('🔇 Escalation monitoring disabled');
      }

      await refreshStatus();

    } catch (error) {
      console.error('Error toggling escalation:', error);
    }
  };

  // Auto-refresh status every 30 seconds
  useEffect(() => {
    const interval = setInterval(refreshStatus, 30000);
    return () => clearInterval(interval);
  }, [automationService, escalationService]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (automationService) {
        automationService.cleanup(incidentId);
      }
      if (escalationService) {
        escalationService.cleanup(incidentId);
      }
    };
  }, [automationService, escalationService, incidentId]);

  if (loading) {
    return (
      <Card 
        title="🤖 Phase 2 Advanced Automation"
        loading={true}
      >
        Initializing advanced automation features...
      </Card>
    );
  }

  return (
    <Card 
      title={
        <Space>
          <ThunderboltOutlined />
          <span>Phase 2 Advanced Automation</span>
          <Badge 
            status={automationEnabled && escalationEnabled ? "processing" : "default"}
            text={automationEnabled && escalationEnabled ? "Active" : "Inactive"}
          />
        </Space>
      }
      extra={
        <Space>
          <Button 
            icon={<SettingOutlined />}
            onClick={refreshStatus}
            size="small"
          >
            Refresh
          </Button>
        </Space>
      }
      style={{ marginBottom: '16px' }}
    >
      {/* MASTER CONTROLS */}
      <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Title level={5} style={{ margin: 0 }}>Master Controls</Title>
          
          <Space>
            <Text strong>Automated Task Completion:</Text>
            <Switch 
              checked={automationEnabled}
              onChange={toggleAutomation}
              checkedChildren="ON"
              unCheckedChildren="OFF"
            />
            {automationStatus?.automationActive && (
              <Tag color="green">Running</Tag>
            )}
          </Space>

          <Space>
            <Text strong>Smart Escalation Engine:</Text>
            <Switch 
              checked={escalationEnabled}
              onChange={toggleEscalation}
              checkedChildren="ON"
              unCheckedChildren="OFF"
            />
            {escalationStatus?.monitoringActive && (
              <Tag color="orange">Monitoring</Tag>
            )}
          </Space>
        </Space>
      </div>

      {/* STATUS OVERVIEW */}
      {(automationStatus || escalationStatus) && (
        <Alert
          type="info"
          message="Automation Status Overview"
          description={
            <Space direction="vertical" style={{ width: '100%' }}>
              {automationStatus && (
                <Space>
                  <RobotOutlined />
                  <Text>
                    Auto-completed {automationStatus.autoCompletedTasks} of {automationStatus.totalTasks} tasks
                    {automationStatus.pendingConfirmations > 0 && 
                      ` • ${automationStatus.pendingConfirmations} pending confirmation`
                    }
                  </Text>
                </Space>
              )}
              {escalationStatus && (
                <Space>
                  <AlertOutlined />
                  <Text>
                    {escalationStatus.activeEscalations} active escalations
                    {escalationStatus.totalEscalations > 0 && 
                      ` • ${escalationStatus.totalEscalations} total escalations logged`
                    }
                  </Text>
                </Space>
              )}
            </Space>
          }
          style={{ marginBottom: '16px' }}
          showIcon
        />
      )}

      {/* TAB INTERFACE */}
      <Tabs 
        activeKey={activeTab} 
        onChange={setActiveTab}
        items={[
          {
            key: 'automation',
            label: (
              <Space>
                <RobotOutlined />
                <span>Automated Tasks</span>
                {automationStatus?.autoCompletedTasks > 0 && (
                  <Badge count={automationStatus.autoCompletedTasks} size="small" />
                )}
              </Space>
            ),
            children: (
              <Card size="small" title="Automated Task Completion Engine">
                {automationStatus ? (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    
                    {/* Automation Rules */}
                    <div>
                      <Text strong>Active Rules:</Text>
                      <div style={{ marginTop: '8px' }}>
                        <Tag color="blue">Incident Status Auto-Completion</Tag>
                        <Tag color="blue">Time-Based Auto-Completion</Tag>
                        <Tag color="blue">Dependency-Based Auto-Completion</Tag>
                        <Tag color="blue">Document Upload Auto-Completion</Tag>
                        <Tag color="blue">Communication Auto-Completion</Tag>
                      </div>
                    </div>

                    {/* Recent Auto-Completions */}
                    {automationStatus.recentAutoCompletions?.length > 0 && (
                      <div>
                        <Text strong>Recent Auto-Completions:</Text>
                        <div style={{ marginTop: '8px' }}>
                          {automationStatus.recentAutoCompletions.map((completion, index) => (
                            <div key={index} style={{ marginBottom: '4px' }}>
                              <Space>
                                <Tag color="green" size="small">AUTO</Tag>
                                <Text>{completion.name}</Text>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  {completion.reason}
                                </Text>
                              </Space>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Pending Confirmations */}
                    {automationStatus.pendingConfirmations > 0 && (
                      <Alert
                        type="warning"
                        message={`${automationStatus.pendingConfirmations} tasks pending manual confirmation`}
                        description="Some tasks were marked for auto-completion but require manual confirmation due to their sensitivity."
                        showIcon
                      />
                    )}

                    {/* Statistics */}
                    <div style={{ 
                      padding: '12px', 
                      backgroundColor: '#f0f9ff', 
                      borderRadius: '6px',
                      border: '1px solid #91d5ff'
                    }}>
                      <Space>
                        <Text strong>Automation Stats:</Text>
                        <Tag color="cyan">{automationStatus.totalTasks} total tasks</Tag>
                        <Tag color="green">{automationStatus.autoCompletedTasks} auto-completed</Tag>
                        <Tag color="orange">{automationStatus.pendingConfirmations} pending</Tag>
                      </Space>
                    </div>

                  </Space>
                ) : (
                  <Text type="secondary">No automation data available</Text>
                )}
              </Card>
            )
          },
          {
            key: 'escalations',
            label: (
              <Space>
                <AlertOutlined />
                <span>Escalations</span>
                {escalationStatus?.activeEscalations > 0 && (
                  <Badge count={escalationStatus.activeEscalations} size="small" />
                )}
              </Space>
            ),
            children: (
              <Card size="small" title="Smart Escalation Engine">
                {escalationStatus ? (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    
                    {/* Escalation Rules */}
                    <div>
                      <Text strong>Monitoring Rules:</Text>
                      <div style={{ marginTop: '8px' }}>
                        <Tag color="red">Critical Task Overdue</Tag>
                        <Tag color="red">Incident Severity Response</Tag>
                        <Tag color="orange">Team Overload</Tag>
                        <Tag color="orange">Communication Breakdown</Tag>
                        <Tag color="orange">External Pressure</Tag>
                      </div>
                    </div>

                    {/* Active Escalations */}
                    {escalationStatus.activeEscalations > 0 && (
                      <Alert
                        type="error"
                        message={`${escalationStatus.activeEscalations} Active Escalations`}
                        description="There are critical issues requiring immediate attention."
                        showIcon
                        action={
                          <Button size="small" danger>
                            View All Escalations
                          </Button>
                        }
                      />
                    )}

                    {/* Recent Escalations */}
                    {escalationStatus.recentEscalations?.length > 0 && (
                      <div>
                        <Text strong>Recent Escalations:</Text>
                        <div style={{ marginTop: '8px' }}>
                          {escalationStatus.recentEscalations.slice(0, 5).map((escalation, index) => (
                            <div key={index} style={{ marginBottom: '8px' }}>
                              <Space direction="vertical" size="small">
                                <Space>
                                  <Tag color={escalation.severity === 'critical' ? 'red' : 'orange'}>
                                    Level {escalation.escalationLevel}
                                  </Tag>
                                  <Text strong>{escalation.reason}</Text>
                                </Space>
                                <Text type="secondary" style={{ fontSize: '12px', marginLeft: '8px' }}>
                                  Escalated to: {escalation.escalateTo?.join(', ')}
                                </Text>
                              </Space>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Escalation Level Breakdown */}
                    <div style={{ 
                      padding: '12px', 
                      backgroundColor: '#fff2e8', 
                      borderRadius: '6px',
                      border: '1px solid #ffbb96'
                    }}>
                      <Space>
                        <Text strong>Escalation Levels:</Text>
                        <Tag color="volcano">Level 1: {escalationStatus.escalationLevels?.level1 || 0}</Tag>
                        <Tag color="red">Level 2: {escalationStatus.escalationLevels?.level2 || 0}</Tag>
                        <Tag color="magenta">Level 3: {escalationStatus.escalationLevels?.level3 || 0}</Tag>
                      </Space>
                    </div>

                  </Space>
                ) : (
                  <Text type="secondary">No escalation data available</Text>
                )}
              </Card>
            )
          },
          {
            key: 'dependencies',
            label: (
              <Space>
                <NodeIndexOutlined />
                <span>Dependencies</span>
              </Space>
            ),
            children: (
              <TaskDependencyMapper 
                incidentId={incidentId} 
                incidentData={incidentData}
              />
            )
          }
        ]}
      />

      {/* FOOTER INFO */}
      <div style={{ 
        marginTop: '16px', 
        padding: '8px', 
        backgroundColor: '#f6ffed', 
        borderRadius: '4px',
        border: '1px solid #b7eb8f'
      }}>
        <Space>
          <ThunderboltOutlined style={{ color: '#52c41a' }} />
          <Text style={{ fontSize: '12px', color: '#52c41a' }}>
            Phase 2 Advanced Automation is monitoring this incident for automated task completion, 
            smart escalations, and dependency conflicts.
          </Text>
        </Space>
      </div>
    </Card>
  );
};

export default Phase2AutomationPanel; 